import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Modal,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';

import { updateProcessSteps } from 'redux/actions';
import { ADDED, PRISTINE, DIRTY } from 'constants/updateRowStatus';

const UpdateSteps = (props) => {

  // We include 'rowId' for the Datagrid while 'id' is the database ID and 'rowStatus'
  // is to track changes. Old but unchanged fields get the row status 'PRISTINE'.
  const [updatedSteps, setUpdatedSteps] = useState(
    props.processInfo.steps.map(step => {
      const vendorInfo = props.vendors.find(vendor => vendor.id === step.vendorId);
      return {
        id: step.id,
        rowId: crypto.randomUUID(),
        rowStatus: PRISTINE,
        userId: props.processInfo.userId,
        vendorId: step.vendorId,
        vendorDisplayId: vendorInfo.displayId,
        vendorName: vendorInfo.name,
        type: step.type,
        cost: step.cost
      };
    })
  );
  
  const [stepCost, setStepCost] = useState(0);
  const [stepInput, setStepInput] = useState({type: "", firstLetter: ""});
  const [vendorInput, setVendorInput] = useState({firstLetter: "", id: 0, displayId: "", name: ""});
  
  const stepNames = useMemo(() => {
    return [
      {type: "Computer", firstLetter: "C"},
      {type: "Crystal", firstLetter: "C"},
      {type: "Hand", firstLetter: "H"},
      {type: "Jhik", firstLetter: "J"},
      {type: "Kaccha", firstLetter: "K"},
      {type: "Patchwork", firstLetter: "P"}
    ]
    .filter(element => !updatedSteps.some(step => element.type === step.type))
  
  }, [updatedSteps]);
  
  const vendorsObj = useMemo(() => {
    const stepVendors = [];

    for ( let i = 0; i < props.vendors.length; i++ ) {
      const vendor = props.vendors[i];
      if (vendor.department === stepInput.type) {
        stepVendors.push({
          firstLetter: vendor.name[0].toUpperCase(),
          id: vendor.id,
          displayId: vendor.displayId,
          name: vendor.name
        });
      }
    }

    return stepVendors.sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));

  }, [props.vendors, stepInput.type]);
  
  const columns = [
    { field: 'type', headerName: 'Type', flex: 1 },
    { field: 'vendorDisplayId', headerName: 'Vendor ID', flex: 1 },
    { field: 'vendorName', headerName: 'Vendor Name', flex: 1 },
    { field: 'cost', headerName: 'Cost', type: 'number', flex: 1, editable: true }
  ];

  const addNewStep = () => {
    // New Steps do not get an 'id' field, as this is the database ID,
    // and they have the row status 'ADDED'.
    setUpdatedSteps([
      ...updatedSteps,
      {
        id: null,
        rowId: crypto.randomUUID(),
        rowStatus: ADDED,
        userId: props.processInfo.userId,
        vendorId: vendorInput.id,
        vendorDisplayId: vendorInput.displayId,
        vendorName: vendorInput.name,
        type: stepInput.type,
        cost: stepCost
      }
    ]);

    setStepCost(0);
    setStepInput({type: "", firstLetter: ""});
    setVendorInput({firstLetter: "", id: 0, displayId: "", name: ""});
  };

  // New row's status is unaffected by edits, but old row's status 
  // get set to 'DIRTY' after edits.
  const getNextRowStatus = (currentRowStatus, action) => {
    switch(action) {
      case 'edit':
        return currentRowStatus === ADDED ? ADDED : DIRTY;
      default:
        return currentRowStatus;
    }
  };

  const handleEditCellChangeCommitted = React.useCallback(
    ({ id, field, props }) => {
      const costIndex = updatedSteps.findIndex(step => step.rowId === id);
      setUpdatedSteps([
        ...updatedSteps.slice(0, costIndex),
        {
          ...updatedSteps[costIndex],
          cost: parseFloat(props.value) || updatedSteps[costIndex].cost,
          rowStatus: getNextRowStatus(updatedSteps[costIndex].rowStatus, 'edit')
        },
        ...updatedSteps.slice(costIndex + 1)
      ]);

    }, [updatedSteps, setUpdatedSteps]
  );

  const saveSteps = () => {
    // New rows will be created in the Database.
    const toCreate = updatedSteps.filter(step => step.rowStatus === ADDED);
    // Old but edited rows will be updated in the Database.
    const toUpdate = updatedSteps.filter(step => step.rowStatus === DIRTY);
    props.updateProcessSteps({
      id: props.processInfo.id,
      toCreate,
      toUpdate
    });

    props.closeModal();
  };

  return (
    <Modal open={props.open}>
      <Card>
        <CardHeader
          subheader="Please update the Steps of the Process."
          title="Update Steps"
        />
        <Divider />
        <CardContent>
          <Box marginTop={2}>
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                md={4}
                xs={12}
              >
                <Autocomplete
                  onChange={(event, newValue) => {
                    if(newValue) setStepInput(newValue);
                    else setStepInput({type: "", firstLetter: ""});
                  }}
                  id="free-solo-dialog-demo"
                  fullWidth
                  options={stepNames}
                  groupBy={(step) => step.firstLetter}
                  getOptionSelected={(option, value) => option.type === value.type}
                  getOptionLabel={(option) => option.type}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Step.."
                      variant="outlined"
                      helperText="Please Select the Step First"
                    />
                  )}
                  value={stepInput ? ( stepInput.type ? stepInput : null ) : null}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <Autocomplete
                  onChange={(event, newValue) => {
                    if(newValue) setVendorInput(newValue);
                    else setVendorInput({firstLetter: "", id: 0, displayId: "", name: ""});
                  }}
                  id="free-solo-dialog-demo"
                  fullWidth
                  options={vendorsObj}
                  groupBy={(vendor) => vendor.firstLetter}
                  getOptionSelected={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Vendor.."
                      variant="outlined"
                      helperText="Please Select the Vendor"
                    />
                  )}
                  value={vendorInput ? ( vendorInput.id ? vendorInput : null ) : null}
                />
              </Grid>
              <Grid
                item
                md={4}
                xs={12}
              >
                <Box
                  display="flex"
                  alignItems="center"
                >
                  <TextField
                    style={{ flexShrink: 1 }}
                    fullWidth
                    label="Cost"
                    onChange={(event) => setStepCost(parseFloat(event.target.value) || 0)}
                    value={stepCost}
                    variant="outlined"
                  />
                  <Button
                    style={{ flexShrink: 0, width: 120, marginLeft: 32, marginRight: 48 }}
                    disabled={!stepInput.type || !vendorInput.id }
                    variant="contained"
                    color="primary"
                    onClick={addNewStep}
                  >
                    Add
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box
              style={{
                height: 370,
                marginTop: '1%'
              }}
            >
              {/* The 'getRowId' function uses the fallback 'row.id' value because of an 
                internal DataGrid bug. */}
              <DataGrid
                rows={updatedSteps}
                columns={columns}
                getRowId={(row) => row.rowId || row.id}
                onEditCellChangeCommitted={handleEditCellChangeCommitted}
                disableSelectionOnClick
                hideFooter
                localeText={{
                  noRowsLabel: 'No steps added'
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2
              }}
            >
              <Button
                disabled={!updatedSteps.some(step => step.rowStatus !== PRISTINE)}
                color="primary"
                variant="contained"
                onClick={saveSteps}
              >
                Save Steps
              </Button>
              <Button sx={{ mx: 1 }} onClick={props.closeModal}>
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
        <Divider />
      </Card>
    </Modal>
  )
};

export default connect (null, {updateProcessSteps}) (UpdateSteps);